import type { SynthPreset } from "../types";

/** Classical / Baroque-inspired pack — instruments for Bach & Mozart lines with a modern synth spin. */
export const CLASSICAL_PRESETS: readonly SynthPreset[] = [
  // Airy traverso: sine body with a breathy noise whisper on osc2, filter barely open, slow LFO shimmer.
  {
    name: "Silver Traverso",
    osc1: { wave: "sine", octave: 0, semis: 0, detuneCents: -3, level: 0.44 },
    osc2: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0.08 },
    filter: { type: "lowpass", cutoffHz: 3200, resonance: 0.12 },
    ampEnv: { a: 0.045, d: 0.2, s: 0.72, r: 0.28 },
    modEnv: { a: 0.06, d: 0.5, s: 0.3, r: 0.3 },
    lfo: { rateHz: 5.4, wave: "sine", depth: 0.3 },
    routes: [
      { source: "lfo", target: "pitch", depth: 0.09 },
      { source: "lfo", target: "amp", depth: 0.06 },
      { source: "modEnv", target: "cutoff", depth: 0.6 },
    ],
    glideMs: 18,
  },
  // Harpsichord pluck: fast mod-env squelch on a narrow pulse, osc2 adds chiff transient via noise burst.
  {
    name: "Candlelight Clavier",
    osc1: { wave: "square", octave: 0, semis: 0, detuneCents: 0, level: 0.46, pwm: 0.18 },
    osc2: { wave: "noise", octave: 1, semis: 0, detuneCents: 0, level: 0.06 },
    filter: { type: "highpass", cutoffHz: 320, resonance: 0.22 },
    ampEnv: { a: 0.001, d: 0.55, s: 0, r: 0.14 },
    modEnv: { a: 0.001, d: 0.08, s: 0, r: 0.04 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.4 },
      { source: "ampEnv", target: "cutoff", depth: 0.9 },
    ],
  },
  // Positif organ: two saws an octave apart mimic drawbar harmonic stacking, full open filter, no release.
  {
    name: "Chapel Positif",
    osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: -5, level: 0.38 },
    osc2: { wave: "saw", octave: 1, semis: 0, detuneCents: 4, level: 0.22 },
    filter: { type: "lowpass", cutoffHz: 4800, resonance: 0.08 },
    ampEnv: { a: 0.018, d: 0.05, s: 0.92, r: 0.04 },
    modEnv: { a: 0.001, d: 0.1, s: 0, r: 0.04 },
    lfo: { rateHz: 5.8, wave: "tri", depth: 0.18 },
    routes: [
      { source: "lfo", target: "amp", depth: 0.05 },
    ],
  },
  // Violin solo: saw body with slow-onset LFO vibrato and mod-env bow attack brightening the filter.
  {
    name: "Baroque Fiddle",
    osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: -7, level: 0.4 },
    osc2: { wave: "saw", octave: 0, semis: 0, detuneCents: 8, level: 0.24 },
    filter: { type: "lowpass", cutoffHz: 2200, resonance: 0.28 },
    ampEnv: { a: 0.06, d: 0.3, s: 0.78, r: 0.35 },
    modEnv: { a: 0.08, d: 0.6, s: 0.4, r: 0.4 },
    lfo: { rateHz: 5.9, wave: "sine", depth: 0.45 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.8 },
      { source: "lfo", target: "pitch", depth: 0.14 },
    ],
    glideMs: 22,
  },
  // Cello continuo: two saws tuned in unison with heavy low-octave weight, warm LP, gentle vibrato.
  {
    name: "Amber Cello",
    osc1: { wave: "saw", octave: -1, semis: 0, detuneCents: -6, level: 0.42 },
    osc2: { wave: "tri", octave: -1, semis: 0, detuneCents: 9, level: 0.24 },
    filter: { type: "lowpass", cutoffHz: 1400, resonance: 0.18 },
    ampEnv: { a: 0.07, d: 0.4, s: 0.8, r: 0.5 },
    modEnv: { a: 0.1, d: 0.7, s: 0.35, r: 0.45 },
    lfo: { rateHz: 5.2, wave: "sine", depth: 0.3 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.2 },
      { source: "lfo", target: "pitch", depth: 0.09 },
    ],
    glideMs: 30,
  },
  // Oboe/reed: bandpass resonance carves out the nasal formant mid-peak; mod-env pushes the bite forward.
  {
    name: "Nasal Hautbois",
    osc1: { wave: "square", octave: 0, semis: 0, detuneCents: -4, level: 0.38, pwm: 0.28 },
    osc2: { wave: "saw", octave: 0, semis: 0, detuneCents: 5, level: 0.2 },
    filter: { type: "bandpass", cutoffHz: 1800, resonance: 0.58 },
    ampEnv: { a: 0.03, d: 0.25, s: 0.68, r: 0.22 },
    modEnv: { a: 0.02, d: 0.35, s: 0.28, r: 0.25 },
    lfo: { rateHz: 5.6, wave: "tri", depth: 0.28 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.1 },
      { source: "lfo", target: "pitch", depth: 0.1 },
    ],
  },
  // Digital harpsipiano hybrid: hammer transient via fast mod-env, medium decay, no sustain after.
  {
    name: "Crystal Fortepiano",
    osc1: { wave: "tri", octave: 0, semis: 0, detuneCents: -3, level: 0.4 },
    osc2: { wave: "square", octave: 1, semis: 0, detuneCents: 6, level: 0.18, pwm: 0.42 },
    filter: { type: "lowpass", cutoffHz: 3600, resonance: 0.2 },
    ampEnv: { a: 0.004, d: 1.1, s: 0.08, r: 0.55 },
    modEnv: { a: 0.001, d: 0.18, s: 0, r: 0.1 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 2.2 },
      { source: "ampEnv", target: "cutoff", depth: 0.7 },
    ],
  },
  // Voix céleste choir pad: two saws gently detuned for that beating shimmer; slow filter swell.
  {
    name: "Celestial Chorale",
    osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: -10, level: 0.28 },
    osc2: { wave: "saw", octave: 0, semis: 0, detuneCents: 11, level: 0.26 },
    filter: { type: "lowpass", cutoffHz: 900, resonance: 0.14 },
    ampEnv: { a: 1.0, d: 1.2, s: 0.8, r: 1.4 },
    modEnv: { a: 1.4, d: 1.8, s: 0.6, r: 1.0 },
    lfo: { rateHz: 0.07, wave: "sine", depth: 0.55 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.6 },
      { source: "lfo", target: "pitch", depth: 0.07 },
      { source: "lfo", target: "cutoff", depth: 0.4 },
    ],
    glideMs: 80,
  },
  // Same choir body with a short glide — better for MIDI choir lines that change notes often.
  {
    name: "Soft Choir",
    osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: -10, level: 0.28 },
    osc2: { wave: "saw", octave: 0, semis: 0, detuneCents: 11, level: 0.26 },
    filter: { type: "lowpass", cutoffHz: 900, resonance: 0.14 },
    ampEnv: { a: 1.0, d: 1.2, s: 0.8, r: 1.4 },
    modEnv: { a: 1.4, d: 1.8, s: 0.6, r: 1.0 },
    lfo: { rateHz: 0.07, wave: "sine", depth: 0.55 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.6 },
      { source: "lfo", target: "pitch", depth: 0.07 },
      { source: "lfo", target: "cutoff", depth: 0.4 },
    ],
    glideMs: 10,
  },
  // Trumpet fanfare: stacked saws, resonant LP punches open on attack, mod-env adds brassy bloom.
  {
    name: "Gilded Clarion",
    osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: -8, level: 0.4 },
    osc2: { wave: "saw", octave: 1, semis: 0, detuneCents: 6, level: 0.2 },
    filter: { type: "lowpass", cutoffHz: 800, resonance: 0.48 },
    ampEnv: { a: 0.025, d: 0.22, s: 0.75, r: 0.2 },
    modEnv: { a: 0.008, d: 0.32, s: 0.18, r: 0.18 },
    lfo: { rateHz: 6.1, wave: "sine", depth: 0.25 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 2.8 },
      { source: "lfo", target: "pitch", depth: 0.08 },
    ],
    glideMs: 12,
  },
  // Celesta/music-box: sine plus an octave-up triangle for bell shimmer, very fast decay, no sustain.
  {
    name: "Ivory Music Box",
    osc1: { wave: "sine", octave: 1, semis: 0, detuneCents: 0, level: 0.45 },
    osc2: { wave: "tri", octave: 2, semis: 0, detuneCents: -4, level: 0.18 },
    filter: { type: "lowpass", cutoffHz: 6000, resonance: 0.3 },
    ampEnv: { a: 0.001, d: 0.65, s: 0, r: 0.45 },
    modEnv: { a: 0.001, d: 0.1, s: 0, r: 0.06 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.0 },
      { source: "modEnv", target: "pitch", depth: 0.5 },
    ],
  },
];
